import { IGRNSubGRNListResponseData } from "@/api_framework/api_modals/Inventory";
import { IPOStatusUpdateModalState } from "@/app/components/routes/modals/POStatusUpdate";
import PWLCalendarComponent from "@/app/components/ui/Calendar";
import Input from "@/app/components/ui/Inputs/Input";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import { useGRNSubGRNListAPI } from "@/app/hooks/api_hooks/Inventory/PO/useGRNSubGRNListAPI";
import { useGrnDetailAPI } from "@/app/hooks/api_hooks/Inventory/PO/useGrnDetailAPI";
import { useInventoryPurchaseOrderUpdateAPI } from "@/app/hooks/api_hooks/Inventory/PO/useInventoryPurchaseOrderUpdateAPI";
import { usePurchaseOrderDetailsAPI } from "@/app/hooks/api_hooks/Inventory/PO/usePurchaseOrderDetailsAPI";
import { useInventoryManufacturereListAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryManufacturereListAPI";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useStringConstants } from "@/app/hooks/useStringConstants";
import { useUtils } from "@/app/hooks/useUtils";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import {
  IInventoryState,
  setActiveGRNId,
  setActivePOId,
  setActivePONumber,
} from "@/app_redux/reducers/slices/auth/inventory_slice";
import { format } from "date-fns";
import { toNumber } from "lodash";
import { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import GoodsReceiptNoteHeaderComponent from "../GoodsReceiptNoteHeaderComponent";
import POClosedModal from "../POClosedModal";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";

export interface IGoodsDoneFormFields {
  subGRNs: IGRNSubGRNListResponseData[];
}

const GoodsReceiptNoteGrnDoneComponent = () => {
  const { getRouteKey } = useRouter();
  const { getCurrentDate } = useUtils();
  const stringConstants = useStringConstants();
  const [params, _setparams] = useSearchParams();
  const {
    activeGRNId,
    GRNDetails,
    purchaseOrderDetails: PODetails,
  } = useRouteInfo(getRouteKey("INVENTORY", "id")).routeState.state as IInventoryState;
  const isGrnDetailInProgess = useLoader("isInventoryGrnDetailInProgress");
  // RBAC: PO_GRN_EDIT_ACTION — legacy semantics: enabled for everyone EXCEPT central_ops.
  // Seed this action for every role except central_ops (clinic_ops, clinic_ops_doggyworld,
  // clinic_ops_bluecity, and any other non-central role that touches GRN).
  const canEditGrnFields = useFeatureAccess("PO_GRN_EDIT_ACTION");
  // RBAC: PO_GRN_CLINIC_MARK_COMPLETE_ACTION — legacy roles: clinic_ops, clinic_ops_doggyworld, clinic_ops_bluecity
  const canMarkGrnComplete = useFeatureAccess("PO_GRN_CLINIC_MARK_COMPLETE_ACTION");
  const { setVisible, setModalData } = useModalActions("PO_STATUS_UPDATE");
  const to_id = params?.get("to_id");
  const po_id = params?.get("po_id");
  const dispatch = useAppDispatch();
  const fetchManufacturers = useInventoryManufacturereListAPI();
  const { execute: fetchCompletedGRNList, subGRNCompleted: completedGRNList } = useGRNSubGRNListAPI();
  const { execute: fetchGrnDetail } = useGrnDetailAPI();
  const updateTOComplete = useInventoryPurchaseOrderUpdateAPI();
  const { execute: fetchPOdetails } = usePurchaseOrderDetailsAPI();
  const [isPOClosedModalOpen, setIsPOClosedModalOpen] = useState(false);

  const formHook = useForm<IGoodsDoneFormFields>({
    mode: "onChange",
    defaultValues: {
      subGRNs: [],
    },
  });

  const goodsDoneFormHook = useFieldArray<IGoodsDoneFormFields>({
    control: formHook.control,
    name: "subGRNs",
    keyName: "id",
  });

  useEffect(() => {
    if (activeGRNId && po_id) {
      fetchCompletedGRNList(activeGRNId);
      fetchManufacturers();
    }
  }, [activeGRNId, po_id]);

  useEffect(() => {
    if (to_id && localStorage.getItem("TransferOrderGRNId")) {
      fetchCompletedGRNList(localStorage.getItem("TransferOrderGRNId"));
      fetchManufacturers();
    }
  }, [localStorage.getItem("TransferOrderGRNId"), to_id]);

  useEffect(() => {
    if (po_id) {
      fetchPOdetails(po_id);
    }
  }, [po_id]);

  useEffect(() => {
    if (po_id) {
      dispatch(setActivePOId(po_id));
      fetchGrnDetail(po_id);
    }
  }, [po_id]);

  useEffect(() => {
    if (GRNDetails) {
      dispatch(setActiveGRNId(GRNDetails?.grn_id));
      dispatch(setActivePONumber(GRNDetails?.po_number));
    }
  }, [GRNDetails]);

  useEffect(() => {
    if (completedGRNList?.length > 0) {
      formHook.setValue("subGRNs", completedGRNList);
    }
  }, [completedGRNList]);

  const onSubmit = async () => {
    setIsPOClosedModalOpen(true);
  };

  const handlePOCloseConfirm = async () => {
    if (to_id) {
      updateTOComplete(null, to_id, null, true).then((res) => {
        if (res?.status) {
          localStorage.setItem("TransferOrderGRNId", null);
          window.open(getRouteKey("TRANSFER_ORDERS", "url"), "_self");
        }
      });
      return;
    }
    const payload: IGRNSubGRNListResponseData[] = formHook.getValues("subGRNs")?.map((subGRN) => {
      return {
        ...subGRN,
        completed_grn: (subGRN?.completed_grn || []).map((product) => ({
          ...product,
          mrp: toNumber(product?.mrp),
          received_packages: toNumber(product?.received_packages),
          package_count: toNumber(product?.package_count),
        })),
      };
    });
    setModalData({
      nextPOStatus: "closed",
      POPayload: PODetails,
      prevPOStatus: PODetails?.po_status,
      GRNId: activeGRNId || localStorage?.getItem("TransferOrderGRNId"),
      GRNPayload: payload,
    } as IPOStatusUpdateModalState);
    setVisible();
  };

  return (
    <>
      <div className="bg-white px-3 py-3 p-4 rounded-md">
        <GoodsReceiptNoteHeaderComponent />
        <div>
          <h3 className="mt-5 px-2 py-3 font-medium text-md">
            {" "}
            GRN ID #{activeGRNId ? activeGRNId : localStorage?.getItem("TransferOrderGRNId")}
          </h3>
          {isGrnDetailInProgess ? (
            <TableSkeleton />
          ) : (
            <form onSubmit={formHook?.handleSubmit(onSubmit)} className="shadow-md overflow-y-auto">
              {goodsDoneFormHook?.fields?.length > 0 ? (
                goodsDoneFormHook?.fields?.map((field, index) => {
                  return (
                    <div key={index}>
                      <p className="mt-4 pl-4 font-semibold text-[1rem]">
                        Sub GRN Id #{String(formHook?.getValues(`subGRNs.${index}.sub_grn_id`))}
                      </p>
                      <div key={field?.id} className="rounded-md min-h-32 max-h-96 overflow-y-scroll mt-3">
                        <table className="min-w-max table-auto border-collapse">
                          <thead>
                            <tr className="bg-gray-200 text-left text-[10px] sm:text-xs 2xl:text-sm font-medium sticky top-0 z-20">
                              <th className="sticky top-0 left-0 z-20 font-medium text-black text-[10px] sm:text-xs 2xl:text-sm px-4 py-2 bg-gray-300">
                                Product Name
                              </th>
                              <th className="sticky top-0 font-medium text-black text-[10px] sm:text-xs 2xl:text-sm px-4 py-2">
                                Qty/Pkg
                              </th>
                              <th className="sticky top-0 font-medium text-black text-[10px] sm:text-xs 2xl:text-sm px-4 py-2">
                                Received Pkgs
                              </th>
                              {!to_id && (
                                <th className="sticky top-0 font-medium text-black text-[10px] sm:text-xs 2xl:text-sm px-4 py-2">
                                  Reason for Short Receive
                                </th>
                              )}
                              <th className="sticky top-0 font-medium text-black text-[10px] sm:text-xs 2xl:text-sm px-4 py-2">
                                Lot No.
                              </th>
                              <th className="sticky top-0 font-medium text-black text-[10px] sm:text-xs 2xl:text-sm px-4 py-2">
                                Manufacturer
                              </th>
                              <th className="sticky top-0 font-medium text-black text-[10px] sm:text-xs 2xl:text-sm px-4 py-2">
                                Exp Date
                              </th>
                              <th className="sticky top-0 font-medium text-black text-[10px] sm:text-xs 2xl:text-sm px-4 py-2">
                                MRP (Tax inclusive)
                              </th>
                              {!to_id && (
                                <>
                                  {" "}
                                  <th className="sticky top-0 font-medium text-black text-[10px] sm:text-xs 2xl:text-sm px-4 py-2">
                                    Cost/Pkg
                                  </th>
                                  <th className="sticky top-0 font-medium text-black text-[10px] sm:text-xs 2xl:text-sm px-4 py-2">
                                    Discount
                                  </th>
                                  <th className="sticky top-0 font-medium text-black text-[10px] sm:text-xs 2xl:text-sm px-4 py-2">
                                    Total Tax
                                  </th>
                                  <th className="sticky top-0 font-medium text-black text-[10px] sm:text-xs 2xl:text-sm px-4 py-2">
                                    Total Cost (inclusive of discount & Tax)
                                  </th>
                                </>
                              )}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-orange-100 mt-3">
                            {formHook?.getValues(`subGRNs.${index}.completed_grn`) &&
                              formHook?.getValues(`subGRNs.${index}.completed_grn`).map((subGRN, subGRNIndex) => {
                                return (
                                  <tr key={subGRNIndex}>
                                    <td
                                      className={`px-3 z-10 py-4 text-xs font-normal break-words w-52 left-0 sticky bg-gray-50 `}
                                    >
                                      <p>{subGRN?.variant_name}</p>
                                    </td>
                                    <td className={`px-3 py-3.5 whitespace-nowrap text-xs font-normal text-center`}>
                                      <p>{subGRN?.qty_per_package || subGRN?.transfer_qty}</p>
                                    </td>
                                    <td className={`px-3 py-3.5 whitespace-nowrap text-xs font-normal`}>
                                      <p>{subGRN?.received_packages}</p>
                                    </td>
                                    {!to_id && (
                                      <td className={`px-3 py-3.5 text-xs font-normal`}>
                                        <p className="break-words w-52">{subGRN?.reason_for_short_receive}</p>
                                      </td>
                                    )}
                                    <td className={`px-3 py-3.5 whitespace-nowrap text-xs font-normal`}>
                                      {PODetails?.po_status === "closed" || to_id ? (
                                        <p>{subGRN?.lot_number}</p>
                                      ) : (
                                        <Input
                                          disabled={!canEditGrnFields}
                                          errorMessages={[]}
                                          className="w-24"
                                          register={formHook.register(
                                            `subGRNs.${index}.completed_grn.${subGRNIndex}.lot_number`,
                                          )}
                                          fieldError={null}
                                        />
                                      )}
                                    </td>
                                    {to_id ? (
                                      <td className={`px-3 py-3.5 whitespace-nowrap text-xs font-normal `}>
                                        <p className="break-words w-52">
                                          {subGRN?.manufacturer_name ?? "Not Available"}
                                        </p>
                                      </td>
                                    ) : (
                                      <td className={`px-3 py-3.5 whitespace-nowrap text-xs font-normal `}>
                                        <p className="break-words w-52">
                                          {subGRN?.manufacturer?.manufacturer_name ?? "Not Available"}
                                        </p>
                                      </td>
                                    )}
                                    <td className="font-normal text-xs whitespace-nowrap">
                                      {PODetails?.po_status === "closed" || to_id ? (
                                        <p>{subGRN?.expiry_date}</p>
                                      ) : (
                                        <PWLCalendarComponent
                                          showCalendarIcon={false}
                                          disabled={!canEditGrnFields}
                                          errorMessages={[]}
                                          register={formHook.register(
                                            `subGRNs.${index}.completed_grn.${subGRNIndex}.expiry_date`,
                                            {
                                              required: false,
                                            },
                                          )}
                                          disableDatesBeforeDate={getCurrentDate()}
                                          fieldError={null}
                                          allowPastDaySelection={false}
                                          showFutureYears={true}
                                          onSelectDate={(date: Date) => {
                                            const formattedDate = format(date, stringConstants?.fieldDOBFormat);
                                            formHook.setValue(
                                              `subGRNs.${index}.completed_grn.${subGRNIndex}.expiry_date`,
                                              formattedDate,
                                            );
                                          }}
                                          className="w-full"
                                        />
                                      )}
                                    </td>
                                    <td className={`px-3 py-3.5 whitespace-nowrap text-xs font-normal`}>
                                      {PODetails?.po_status === "closed" || to_id ? (
                                        <p>{subGRN?.mrp}</p>
                                      ) : (
                                        <Input
                                          className="w-24"
                                          disabled={!canEditGrnFields}
                                          errorMessages={[]}
                                          type="number"
                                          register={formHook.register(
                                            `subGRNs.${index}.completed_grn.${subGRNIndex}.mrp`,
                                          )}
                                          fieldError={null}
                                        />
                                      )}
                                    </td>
                                    {!to_id && (
                                      <>
                                        <td
                                          className={`px-3 py-3.5 whitespace-nowrap text-xs font-normal text-center `}
                                        >
                                          <p>{subGRN?.price_per_package}</p>
                                        </td>
                                        <td
                                          className={`px-3 py-3.5 whitespace-nowrap text-xs font-normal text-center `}
                                        >
                                          <p>{subGRN?.discount_percentage}%</p>
                                        </td>
                                        <td
                                          className={`px-3 py-3.5 whitespace-nowrap text-xs font-normal text-center `}
                                        >
                                          <p>{typeof subGRN?.tax_details === "string" ? subGRN.tax_details : "null"}</p>
                                        </td>
                                        <td
                                          className={`px-3 py-3.5 whitespace-nowrap text-xs font-normal text-center `}
                                        >
                                          <p>{subGRN?.total_cost_after_tax}</p>
                                        </td>
                                      </>
                                    )}
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="py-4 text-center text-gray-500">No data found</p>
              )}
              {PODetails?.po_status === "closed" ? null : (
                <div className="bottom-0 sticky flex justify-end items-center bg-white p-2 text-xs">
                  {canMarkGrnComplete && PODetails?.mark_grn_complete && (
                      <input
                        className="bg-blue-900 px-2 py-2 rounded-md text-white cursor-pointer"
                        type="submit"
                        value={"Mark GRN Complete"}
                      />
                    )}
                </div>
              )}
              {PODetails?.po_status === "closed" ? null : (
                <div className="bottom-0 sticky flex justify-end items-center bg-white p-2 text-xs">
                  {canMarkGrnComplete && to_id && GRNDetails?.pending_grn?.length === 0 && (
                      <input
                        className="bg-blue-900 px-2 py-2 rounded-md text-white cursor-pointer"
                        type="submit"
                        value={"Mark GRN Complete"}
                      />
                    )}
                </div>
              )}
            </form>
          )}
        </div>
        <POClosedModal
          open={isPOClosedModalOpen}
          setOpen={setIsPOClosedModalOpen}
          onConfirmClose={handlePOCloseConfirm}
        />
      </div>
    </>
  );
};

export default GoodsReceiptNoteGrnDoneComponent;
